import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem
} from "../basic/sidebar";
import {
  CreditCard,
  Download,
  ExternalLink,
  HelpCircle,
  LayoutDashboard,
  Loader2,
  LogOut,
  Package,
  Settings,
  Shield,
  ShoppingCart,
  Users
} from "lucide-react";
import {UserInfo} from "../shared/UserInfo";
import {useTranslationWithKeys} from "../../hooks/useTranslationWithKeys";
import {useCreateBillingPortalSessionMutation, useGetPortalTeamQuery} from "../../store/generated/api";
import {toast} from "sonner";
import {getTeamDisplayName} from "../../utils/teamUtils";
import polysunLogo from "../../assets/Polysun-Software-Icon.svg";
import {Button} from "../basic/button";

interface UserSidebarProps {
  user: any;
  location: any;
  navigate: (path: string) => void;
  onEnterAdminMode: () => void;
  onLogout: () => void;
}

export function UserSidebar({ user, location, navigate, onEnterAdminMode, onLogout }: UserSidebarProps) {
  const {t} = useTranslationWithKeys();
  const [createBillingPortalSession, {isLoading: isBillingLoading}] = useCreateBillingPortalSessionMutation();
  const {data: teamData} = useGetPortalTeamQuery({page: 0, size: 10});
  const team = teamData?.team;
  const teamDisplayName = getTeamDisplayName({
    teamName: team?.name,
    firstName: user?.firstName,
    lastName: user?.lastName
  }) ?? t('common.noTeam');

  const handleBillingClick = async () => {
    try {
      const response = await createBillingPortalSession({
        vwpBillingPortalRequest: {
          returnUrl: window.location.href
        }
      }).unwrap();

      if (response.sessionUrl) {
        window.open(response.sessionUrl, '_blank');
      }
    } catch (error) {
      toast.error(t('sidebar.billingError', {defaultValue: 'Failed to open billing portal'}));
    }
  };

  const hasManagerRole = user?.roles?.some(role => ['ADMIN', 'PLATFORM_ADMIN'].includes(role));
  
  const menuItems = [
    {id: "/dashboard", label: t('sidebar.dashboard'), icon: LayoutDashboard, requireManager: true},
    {id: "/licenses", label: t('sidebar.licenses'), icon: Package, requireManager: true},
    {id: "/team", label: t('sidebar.teamMembers'), icon: Users, requireManager: true},
    {id: "/download", label: t('sidebar.download'), icon: Download, requireManager: false},
    {id: "/pricing", label: t('sidebar.pricing'), icon: ShoppingCart, requireManager: true},
    {id: "/support", label: t('sidebar.support'), icon: HelpCircle, requireManager: false},
    {id: "/settings", label: t('sidebar.settings'), icon: Settings, requireManager: false},
  ];

  return (
    <Sidebar variant="sidebar" collapsible="offcanvas" className="z-50">
      <SidebarHeader className="border-b p-4">
        <div className="flex items-center gap-2 mb-4">
          <img src={polysunLogo} alt="Polysun" className="h-8 w-8 shrink-0"/>
          <div className="flex-1 min-w-0">
            <p className="truncate">{t('sidebar.teamPortal')}</p>
            <p className="text-muted-foreground truncate" title={teamDisplayName}>
              {teamDisplayName}
            </p>
          </div>
        </div>
        <UserInfo user={user} onNavigate={navigate}/>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>{t('sidebar.mainMenu')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems.filter(item => !item.requireManager || hasManagerRole).map((item) => (
                <SidebarMenuItem key={item.id}>
                  <SidebarMenuButton
                    onClick={() => navigate(item.id)}
                    isActive={location.pathname === item.id}
                  >
                    <item.icon className="h-4 w-4" />
                    <span>{item.label}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="border-t p-4">
        {user?.roles?.includes('PLATFORM_ADMIN') && (
            <Button
                variant="ghost"
                className="flex items-center gap-2 w-full justify-start px-3 py-2 mb-3"
                onClick={onEnterAdminMode}
            >
              <Shield className="h-4 w-4"/>
              <span>{t('sidebar.admin')}</span>
            </Button>
        )}
        <Button
            variant="ghost"
            className="flex items-center gap-2 w-full justify-start px-3 py-2 mb-3 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleBillingClick}
            disabled={isBillingLoading}
        >
          {isBillingLoading ? (
              <Loader2 className="h-4 w-4 animate-spin"/>
          ) : (
              <CreditCard className="h-4 w-4"/>
          )}
          <span>{t('sidebar.billing')}</span>
          {!isBillingLoading && <ExternalLink className="h-3 w-3 ml-auto"/>}
        </Button>
        <Button
            variant="ghost"
            className="flex items-center gap-2 w-full justify-start px-3 py-2"
          onClick={onLogout}
        >
          <LogOut className="h-4 w-4" />
          <span>{t('sidebar.signOut')}</span>
        </Button>
      </SidebarFooter>
    </Sidebar>
  );
}